import {ssrSafeLocation} from '@github-ui/ssr-utils'
import type {ActivityFilter, PathFunction, Repository, RepositoryPathAction, RepositoryTreePathAction} from './types'
import {encodePart, parameterize} from './utils'

export * from './github-models'
export * from './models-byok'

export const fullUrl: PathFunction<{path: string}> = ({path}) => new URL(path, ssrSafeLocation.origin).toString()

export const codeNavGeneralSearchPath: PathFunction<{searchTerm: string}> = ({searchTerm}) =>
  `/search?q=${encodePart(`${searchTerm}`)}&type=code`

export const codeNavSearchPath: PathFunction<{owner: string; repo: string; searchTerm: string}> = ({
  owner,
  repo,
  searchTerm,
}) => `/search?q=${encodePart(`repo:${owner}/${repo} ${searchTerm}`)}&type=code`

export const searchPath: PathFunction = () => `/search`

export const advancedSearchPath = () => `/search/advanced`

export const searchStatsPath: PathFunction = () => `/search/stats`

export const ownerPath: PathFunction<{owner: string}> = ({owner}) => `/${encodePart(owner)}`

export const ownerAvatarPath: PathFunction<{owner: string}> = ({owner}) => `/${encodePart(owner)}.png`

export const copilotHovercardPath: PathFunction<{bot_slug: string}> = ({bot_slug}) =>
  `/copilot/hovercard?bot=${encodePart(bot_slug)}`

export const copilotOrgSettingsModels: PathFunction<{org: string}> = ({org}) =>
  `/organizations/${encodePart(org)}/settings/copilot/models`

export const userHovercardPath: PathFunction<{owner: string}> = ({owner}) => `/users/${encodePart(owner)}/hovercard`

export const teamPath: PathFunction<{owner: string; team: string}> = ({owner, team}) =>
  `/orgs/${encodePart(owner)}/teams/${encodePart(team)}`

export const teamHovercardPath: PathFunction<{owner: string; team: string}> = ({owner, team}) =>
  `/orgs/${encodePart(owner)}/teams/${encodePart(team)}/hovercard`

export const orgHovercardPath: PathFunction<{owner: string}> = ({owner}) => `/orgs/${encodePart(owner)}/hovercard`

export const userIdHovercardPath: PathFunction<{userId: number}> = ({userId}) => `/hovercards?user_id=${userId}`

export function repositoryPath({owner, repo, action}: {owner: string; repo: string; action?: RepositoryPathAction}) {
  return action ? `/${encodePart(owner)}/${encodePart(repo)}/${action}` : `/${encodePart(owner)}/${encodePart(repo)}`
}

export const repositoryStatsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/stats`

export const repositoryDeploymentsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/deployments`

export const repositoryDeployPath: PathFunction<{
  owner: string
  repo: string
  framework?: string
  packageManager?: string
}> = ({owner, repo, framework, packageManager}) => {
  const basePath = `/${encodePart(owner)}/${encodePart(repo)}/deploy`
  const params = new URLSearchParams()

  if (framework) {
    params.append('framework', framework)
  }
  if (packageManager) {
    params.append('package_manager', packageManager)
  }

  return params.toString() ? `${basePath}?${params.toString()}` : basePath
}

export const repositoryParticipationPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/graphs/participation`

export const repoCommitActivityInsightsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/graphs/commit-activity`

export const refNameCheckPath: PathFunction<{owner: string; repo: string; refName: string}> = ({
  owner,
  repo,
  refName,
}) => `/${encodePart(owner)}/${encodePart(repo)}/branches/${encodePart(refName)}/rename_ref_check`

export const renameBranchEffectsPath: PathFunction<{owner: string; repo: string; branchName: string}> = ({
  owner,
  repo,
  branchName,
}) => `/${encodePart(owner)}/${encodePart(repo)}/branches/rename_form/${encodePart(branchName)}`

export const renameRefPath: PathFunction<{owner: string; repo: string; refName: string}> = ({owner, repo, refName}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/branches/${encodePart(refName)}`

export const checkTagNameExistsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/branches/check_tag_name_exists`

export const checkTagStatePath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/tags/check`

export const branchPath: PathFunction<{owner: string; repo: string; branch: string}> = ({owner, repo, branch}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/tree/${encodePart(branch)}`

export const tagPath: PathFunction<{owner: string; repo: string; tag: string}> = ({owner, repo, tag}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/releases/tag/${encodePart(tag)}`

export const invitationsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/invitations`

export const migrateImmutableActionsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/actions/immutable_actions/migrate`

export const migrationWorkflowsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/actions/workflows/immutable-actions-migration/migrate_release`

export const newBranchProtectionPath: PathFunction<{owner: string; repo: string; branchName: string}> = ({
  owner,
  repo,
  branchName,
}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/settings/branch_protection_rules/new?branch_name=${encodePart(branchName)}`

export const rulesetsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/settings/rules`

export const newRulesetPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/settings/rules/new?target=branch&enforcement=disabled`

export const orgBlockedUsersPath: PathFunction<{owner: string}> = ({owner}) =>
  `/organizations/${encodePart(owner)}/settings/blocked_users`

export const orgRulesetsTargetCountPath: PathFunction<{owner: string}> = ({owner}) =>
  `/organizations/${encodePart(owner)}/settings/rules/deferred_target_counts`

export const stafftoolsOrgRulesetsTargetCountPath: PathFunction<{owner: string}> = ({owner}) =>
  `/stafftools/users/${encodePart(owner)}/organization_rules/deferred_target_counts`

export const repoRulesetsTargetCountPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/settings/rules/deferred_target_counts`

export const stafftoolsRepoRulesetsTargetCountPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/stafftools/repositories/${encodePart(owner)}/${encodePart(repo)}/repository_rules/deferred_target_counts`

export const validateRegexPath = () => '/repos/validate_regex'
export const validateRegexValuePath = () => '/repos/validate_regex/value'

export const blobPath: PathFunction<{
  owner: string
  repo: string
  commitish: string
  filePath: string
  lineNumber?: number
  plain?: number
  endLineNumber?: number
}> = ({owner, repo, commitish, filePath, lineNumber, plain, endLineNumber}) => {
  const params = plain ? `?plain=${plain}` : ''
  const suffix = lineNumber ? `#L${lineNumber}${endLineNumber ? `-L${endLineNumber}` : ''}` : ''
  return `/${encodePart(owner)}/${encodePart(repo)}/blob/${encodePart(commitish)}/${encodePart(
    filePath,
  )}${params}${suffix}`
}

export const deleteBlobPath: PathFunction<{
  owner: string
  repo: string
  commitish: string // must be a branch name
  filePath: string
}> = ({owner, repo, commitish, filePath}) => {
  return `/${encodePart(owner)}/${encodePart(repo)}/delete/${encodePart(commitish)}/${encodePart(filePath)}`
}

export const editBlobPath: PathFunction<{
  owner: string
  repo: string
  commitish: string // must be a branch name
  filePath: string
  hash?: string
  lineNumber?: number
  plain?: number
  returnToPrPath?: string
}> = ({owner, repo, commitish, filePath, hash, lineNumber, returnToPrPath}) => {
  const suffix = hash ? hash : lineNumber ? `#L${lineNumber}` : ''
  const returnToPr = returnToPrPath ? `?pr=${encodeURI(returnToPrPath)}` : ''
  return `/${encodePart(owner)}/${encodePart(repo)}/edit/${encodePart(commitish)}/${encodePart(
    filePath,
  )}${suffix}${returnToPr}`
}

export const blamePath: PathFunction<{
  owner: string
  repo: string
  commitish: string
  filePath: string
  lineNumber?: number
}> = ({owner, repo, commitish, filePath, lineNumber}) => {
  const suffix = lineNumber ? `#L${lineNumber}` : ''
  return `/${encodePart(owner)}/${encodePart(repo)}/blame/${encodePart(commitish)}/${encodePart(filePath)}${suffix}`
}

export function dismissRepositoryNoticePathPath({login}: {login: string}) {
  return `/users/${encodePart(login)}/dismiss_repository_notice`
}

export function deferredMetadataPath({repo, commitish, path}: {repo: Repository; commitish: string; path: string}) {
  const parts = ['', repo.ownerLogin, repo.name, 'deferred-metadata', commitish, path]
  return parts.map(encodePart).join('/')
}

export function deferredASTPath({repo, commitish, path}: {repo: Repository; commitish: string; path: string}) {
  const parts = ['', repo.ownerLogin, repo.name, 'deferred-ast', commitish, path]
  return parts.map(encodePart).join('/')
}

export function repoOverviewUrl(repo: Pick<Repository, 'name' | 'ownerLogin'>) {
  return `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}`
}

export function repoDefaultBrachUrl(repo: Repository) {
  return `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}?files=1`
}

export function repositoryTreePath({
  repo,
  commitish,
  action,
  path,
}: {
  repo: Pick<Repository, 'name' | 'ownerLogin'>
  commitish: string
  action: RepositoryTreePathAction
  path?: string
}) {
  const parts = ['', repo.ownerLogin, repo.name, action, commitish]

  if (path && path !== '/') {
    parts.push(path)
  }

  return parts.map(encodePart).join('/')
}

export const blobSidePanelMetadataPath: PathFunction<{
  owner: string
  repo: string
}> = ({owner, repo}) => `/${owner}/${repo}/sidepanel-metadata`

export function blobDetectLanguage(repo: Repository, encodedFileName: string, fullDetails?: boolean) {
  return `/${repo.ownerLogin}/${repo.name}/detect_language?filename=${encodedFileName}${
    fullDetails ? '&full_details=true' : ''
  }`
}

export const wikiBlobBasePath: PathFunction<{owner: string; repo: string; filePath: string}> = ({
  owner,
  repo,
  filePath,
}) => `/${encodePart(owner)}/${encodePart(repo)}/wiki/${filePath.substring(0, filePath.lastIndexOf('.'))}`

export const wikiBlobPath: PathFunction<{owner: string; repo: string; filePath: string; commitish: string}> = ({
  owner,
  repo,
  filePath,
  commitish,
}) => `${wikiBlobBasePath({owner, repo, filePath})}/${commitish}`

export const wikiComparePath: PathFunction<{owner: string; repo: string; commitish: string}> = ({
  owner,
  repo,
  commitish,
}) => `/${encodePart(owner)}/${encodePart(repo)}/wiki/_compare/${commitish}`

export const commitPath: PathFunction<{owner: string; repo: string; commitish: string}> = ({owner, repo, commitish}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/commit/${commitish}`

export const diffLinesPath: PathFunction<{owner: string; repo: string; sha1: string; sha2: string; entry: string}> = ({
  owner,
  repo,
  entry,
  sha1,
  sha2,
}) => `/${encodePart(owner)}/${encodePart(repo)}/diffs/${entry}/diff-lines?sha1=${sha1}&sha2=${sha2}`

export const diffIndexPath: PathFunction<{owner: string; repo: string; sha1: string; sha2: string; oid: string}> = ({
  owner,
  repo,
  sha1,
  sha2,
  oid,
}) => `/${encodePart(owner)}/${encodePart(repo)}/diffs?commit=${oid}&sha2=${sha2}${sha1 != null ? `&sha1=${sha1}` : ''}`
export const commitContextLinesPath: PathFunction<{owner: string; repo: string; commitish: string}> = ({
  owner,
  repo,
  commitish,
}) => `${commitPath({owner, repo, commitish})}/context_lines`

export const commitDiscussionsPath: PathFunction<{
  owner: string
  repo: string
  commitOid: string
  beforeCommentId?: string
}> = ({owner, repo, commitOid, beforeCommentId}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/commit/${commitOid}/discussion_comments${
    beforeCommentId ? `?before_comment_id=${beforeCommentId}` : ''
  }`

export const commitInlineCommentsPath: PathFunction<{
  owner: string
  repo: string
  commitOid: string
  path: string
  position: string
}> = ({owner, repo, commitOid, path, position}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/commit/${commitOid}/inline_comments/?path=${encodePart(
    path,
  )}&position=${encodePart(position)}`

export const commitCommentDeferredCommentDataPath: PathFunction<{
  owner: string
  repo: string
  commitOid: string
  untilCommentId?: string
}> = ({owner, repo, commitOid, untilCommentId}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/commit/${commitOid}/deferred_comment_data${
    untilCommentId ? `?until_comment_id=${untilCommentId}` : ''
  }`

export const commitsPath: PathFunction<{owner: string; repo: string; ref?: string; path?: string}> = ({
  owner,
  repo,
  ref,
  path,
}) => {
  const base = `/${encodePart(owner)}/${encodePart(repo)}/commits`
  if (!ref) return base
  if (!path) return `${base}/${encodePart(ref)}`
  return `${base}/${encodePart(ref)}/${encodePart(stripLeadingSlash(path))}`
}

export function commitsPathByAuthor({
  repo,
  branch,
  path,
  author,
}: {
  repo: Repository
  branch: string
  path: string
  author: string
}) {
  const url = [repo.ownerLogin, repo.name, 'commits', branch, path].map(encodePart).join('/')
  return `/${url}?author=${encodePart(author)}`
}

export function commitStatusDetailsPath(repo: Repository, oid: string) {
  return `/${repo.ownerLogin}/${repo.name}/commit/${oid}/status-details`
}

export function commitsByAuthor({repo, author}: {repo: Repository; author: string}) {
  const parts = [repo.ownerLogin, repo.name]

  return `/${parts.map(encodePart).join('/')}/commits?author=${encodePart(author)}`
}

export const commitHovercardPath: PathFunction<{owner: string; repo: string; commitish: string}> = ({
  owner,
  repo,
  commitish,
}) => `/${encodePart(`${owner}/${repo}/commit/${commitish}/hovercard`)}`

export const branchCommmitsPath: PathFunction<{owner: string; repo: string; commitish: string}> = ({
  owner,
  repo,
  commitish,
}) => `/${encodePart(`${owner}/${repo}/branch_commits/${commitish}`)}`

export const topicPath: PathFunction<{topicName: string}> = ({topicName}) => `/topics/${topicName}`

export const marketplaceCategoryPath: PathFunction<{categoryName: string}> = ({categoryName}) =>
  `/marketplace/category/${parameterize(categoryName)}`

export const marketplaceActionPath: PathFunction<{slug: string}> = ({slug}) =>
  `/marketplace/actions/${encodePart(slug)}`

export const actionsWorkflowRunPath: PathFunction<{owner: string; repo: string; runId?: string; attempt?: string}> = ({
  owner,
  repo,
  runId,
  attempt,
}) => {
  const parts = [owner, repo, 'actions']
  if (runId) {
    parts.push('runs', runId)
  }
  // Attempt defaults to '1' when not set
  if (runId && attempt) {
    parts.push('attempts', attempt)
  }

  return `/${parts.map(encodePart).join('/')}`
}

export const actionsWorkflowFilePath: PathFunction<{owner: string; repo: string; runId?: string}> = ({
  owner,
  repo,
  runId,
}) => {
  const parts = [owner, repo, 'actions']
  if (runId) {
    parts.push('runs', runId, 'workflow')
  }
  return `/${parts.map(encodePart).join('/')}`
}

export const actionsWorkflowPath: PathFunction<{owner: string; repo: string; workflowFileName: string}> = ({
  owner,
  repo,
  workflowFileName,
}) => `/${encodePart(owner)}/${encodePart(repo)}/actions/workflows/${encodePart(workflowFileName)}`

export const codeownersErrorPath: PathFunction<{owner: string; repo: string; commitish: string; filePath: string}> = ({
  owner,
  repo,
  commitish,
  filePath,
}) => `/${owner}/${repo}/codeowners-validity/${commitish}/${filePath}`

function stripLeadingSlash(path: string) {
  return path.startsWith('/') ? path.slice(1) : path
}

export function parentPath(path: string) {
  return path.split('/').slice(0, -1).join('/')
}

/**
 * Returns the path to the activity index page for a repository.
 *
 * To reuse this path in apps rendered in different places (e.g. GitHub.com vs Stafftools) - pass in the `baseUrl` param.
 */
export function activityIndexPath({
  repo,
  baseUrl = '',
  branch,
  filter,
  pagination,
  perPage = 30,
}: {
  repo: Repository
  baseUrl?: string
  branch?: string
  filter?: ActivityFilter
  pagination?: {before?: string | null; after?: string | null}
  perPage?: number
}) {
  const parts = [repo.ownerLogin, repo.name, 'activity']
  const params = []

  if (branch) params.push(`ref=${encodePart(branch)}`)
  if (filter) {
    // Don't add default values to the path/query-string
    if (filter.activityType && filter.activityType.toLocaleLowerCase() !== 'all') {
      params.push(`activity_type=${encodePart(filter.activityType)}`)
    }

    if (filter.actor?.login) {
      params.push(`actor=${encodePart(filter.actor.login)}`)
    }

    // Don't add default values to the path/query-string
    if (filter.timePeriod && filter.timePeriod.toLocaleLowerCase() !== 'all') {
      params.push(`time_period=${encodePart(filter.timePeriod)}`)
    }

    // Don't add default values to the path/query-string
    if (filter.sort && filter.sort.toLocaleLowerCase() !== 'desc') {
      params.push(`sort=${encodePart(filter.sort)}`)
    }
  }

  if (pagination) {
    if (pagination.after) {
      params.push(`after=${encodePart(pagination.after)}`)
    } else if (pagination.before) {
      params.push(`before=${pagination.before}`)
    }
  }

  // Don't add default values to the path/query-string
  if (perPage && perPage !== 30) {
    params.push(`per_page=${perPage}`)
  }

  return `${baseUrl}/${parts.map(encodePart).join('/')}${params.length > 0 ? `?${params.join('&')}` : ''}`
}

/**
 * Returns the path to the activity actors endpoing for a repository.
 *
 * To reuse this path in apps rendered in different places (e.g. GitHub.com vs Stafftools) - pass in the `baseUrl` param.
 */
export function activityActorsPath({
  repo,
  baseUrl = '',
  branch,
  timePeriod,
}: {
  repo: Repository
  baseUrl?: string
  branch?: string
  timePeriod?: string
}) {
  const parts = [repo.ownerLogin, repo.name, 'activity', 'actors']
  const params = []

  if (branch) params.push(`ref=${encodePart(branch)}`)
  if (timePeriod) params.push(`time_period=${encodePart(timePeriod)}`)

  return `${baseUrl}/${parts.map(encodePart).join('/')}${params.length > 0 ? `?${params.join('&')}` : ''}`
}

export function ruleInsightsActorsPath() {
  return `insights/actors`
}

export function bypassRequestersActorsPath() {
  return `bypass_requests/requesters`
}

export function bypassApproversActorsPath() {
  return `bypass_requests/approvers`
}

export function comparePath({repo, base = undefined, head}: {repo: Repository; base?: string; head: string}) {
  const baseSegment = [repo.ownerLogin, repo.name, 'compare'].map(encodePart).join('/')
  const compareSegment = base ? `${encodePart(base)}...${encodePart(head)}` : encodePart(head)
  return `/${baseSegment}/${compareSegment}`
}

export function mergeabilityCheckPath({repo, base, head}: {repo: Repository; base: string; head: string}) {
  const baseSegment = [repo.ownerLogin, repo.name, 'branches', 'pre_mergeable'].map(encodePart).join('/')
  const compareSegment = `${encodePart(base)}...${encodePart(head)}`
  return `/${baseSegment}/${compareSegment}`
}

// Consider using /feature_preview/:feature_id/toggle instead
export function toggleGenericFeaturePath({repo}: {repo: Repository}) {
  const parts = [repo.ownerLogin, repo.name, 'toggle_generic_feature']
  return `/${parts.map(encodePart).join('/')}`
}

export function repoPullRequestsPath(owner: string, repo: string) {
  return `/${encodePart(owner)}/${encodePart(repo)}/pulls`
}

export function repoStargazersPath(owner: string, repo: string) {
  return `/${encodePart(owner)}/${encodePart(repo)}/stargazers`
}

export function newPullRequestPath({repo, refName}: {repo: Repository; refName: string}) {
  return `/${[repo.ownerLogin, repo.name, 'pull', 'new', refName].map(encodePart).join('/')}`
}

export function pullRequestPath({repo, number}: {repo: Repository; number: number}) {
  return `/${[repo.ownerLogin, repo.name, 'pull', number.toString()].map(encodePart).join('/')}`
}

export const pullRequestTitlePath: PathFunction<{
  owner: string
  repo: string
  number: number
}> = ({owner, repo, number}) => `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}`

export const pullRequestBodyPath: PathFunction<{
  owner: string
  repo: string
  number: number
  contentId: number
}> = ({owner, repo, number, contentId}) => `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}#issue-${contentId}`

export const pullRequestCommentPath: PathFunction<{
  owner: string
  repo: string
  number: number
  contentId: number
}> = ({owner, repo, number, contentId}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}#issuecomment-${contentId}`

export const pullRequestReviewPath: PathFunction<{
  owner: string
  repo: string
  number: number
  contentId: number
}> = ({owner, repo, number, contentId}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}#pullrequestreview-${contentId}`

export const pullRequestReviewCommentPath: PathFunction<{
  owner: string
  repo: string
  number: number
  contentId: number
}> = ({owner, repo, number, contentId}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}#discussion_r${contentId}`

export const pullRequestFilesChangedPath: PathFunction<{
  owner: string
  repo: string
  number: number
}> = ({owner, repo, number}) => `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/files`

export const pullRequestFilesChangedCommitPath: PathFunction<{
  owner: string
  repo: string
  number: number
  commit: string
}> = ({owner, repo, number, commit}) => `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/commits/${commit}`

export const pullRequestFilesChangedCommitRangePath: PathFunction<{
  owner: string
  repo: string
  number: number
  base?: string
  head: string
}> = ({owner, repo, number, base, head}) => {
  if (base) {
    return `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/files/${base}..${head}`
  } else {
    return `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/files/${head}`
  }
}

export const pullRequestChangesPath: PathFunction<{
  owner: string
  repo: string
  number: number
}> = ({owner, repo, number}) => `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/changes`

export const pullRequestChangesCommitRangePath: PathFunction<{
  owner: string
  repo: string
  number: number
  base?: string
  head: string
}> = ({owner, repo, number, base, head}) => {
  if (base) {
    return `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/changes/${base}..${head}`
  } else {
    return `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/changes/${head}`
  }
}

export const pullRequestCopilotDiffChatPath: PathFunction<{
  owner: string
  repo: string
  number: number
  baseOid: string
  headOid: string
}> = ({owner, repo, number, baseOid, headOid}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/copilot_diff_chat/?base_oid=${baseOid}&head_oid=${headOid}`

export const pullRequestProcessingIndicatorPath: PathFunction<{
  owner: string
  repo: string
  number: number
}> = ({owner, repo, number}) => {
  return `/${encodePart(owner)}/${encodePart(repo)}/pull/${number}/partials/processing_indicator`
}

export function fetchAndMergePath({repo, refName, discard}: {repo: Repository; refName: string; discard?: boolean}) {
  return `/${[repo.ownerLogin, repo.name, 'branches', 'fetch_and_merge', refName].map(encodePart).join('/')}${
    discard ? '?discard_changes=true' : ''
  }`
}

export function reposSurveyPath(repo: Repository, action?: 'dismiss' | 'answer') {
  const parts = [repo.ownerLogin, repo.name, 'repos', 'survey']
  if (action) parts.push(action)
  return `/${parts.map(encodePart).join('/')}`
}

export function codeNavSurveyPath(ownerLogin: string, repoName: string, action?: 'answer') {
  const parts = [ownerLogin, repoName, 'repos', 'code_nav_survey']
  if (action) parts.push(action)
  return `/${parts.map(encodePart).join('/')}`
}

export function treeListPath({
  repo,
  commitOid,
  includeDirectories = false,
}: {
  repo: Repository
  commitOid: string
  includeDirectories?: boolean
}) {
  const path = `/${[repo.ownerLogin, repo.name, 'tree-list', commitOid].map(encodePart).join('/')}`

  return includeDirectories ? `${path}?include_directories=${includeDirectories}` : path
}

export function branchCountPath(repo: Repository) {
  const parts = [repo.ownerLogin, repo.name, 'branch-count']
  return `/${parts.map(encodePart).join('/')}`
}

export function tagCountPath(repo: Repository) {
  const parts = [repo.ownerLogin, repo.name, 'tag-count']
  return `/${parts.map(encodePart).join('/')}`
}

export function branchAndTagCountPath(repo: Repository) {
  const parts = [repo.ownerLogin, repo.name, 'branch-and-tag-count']
  return `/${parts.map(encodePart).join('/')}`
}

export function codeNavigationPath({
  repo,
  type,
  q,
  language,
  row,
  column,
  ref,
  path,
  codeNavContext,
  symbolKind,
}: {
  repo: Repository
  type: string
  q: string
  language: string
  row: number
  column: number
  ref: string
  path: string
  codeNavContext: string
  symbolKind: string | null
}) {
  const repoSegment = [repo.ownerLogin, repo.name].map(encodePart).join('/')
  const params = new URLSearchParams()
  params.append('q', q)
  params.append('language', language)
  params.append('row', String(row))
  params.append('col', String(column))
  params.append('ref', ref)
  params.append('blob_path', path)
  params.append('code_nav_context', codeNavContext)
  if (symbolKind) {
    params.append('symbol_kind', symbolKind)
  }
  return `/${repoSegment}/find-react-${type}?${params.toString()}`
}

export const discussionsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/discussions`

export const discussionPath: PathFunction<{owner: string; repo: string; discussionNumber: number}> = ({
  owner,
  repo,
  discussionNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/discussions/${discussionNumber}`

export const discussionCommentPath: PathFunction<{
  owner: string
  repo: string
  discussionNumber: number
  contentId: number
}> = ({owner, repo, discussionNumber, contentId}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/discussions/${discussionNumber}#discussioncomment-${contentId}`

export const discussionBodyPath: PathFunction<{
  owner: string
  repo: string
  discussionNumber: number
  contentId: number
}> = ({owner, repo, discussionNumber, contentId}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/discussions/${discussionNumber}#discussion-${contentId}`

export function repoIssuesPath(owner: string, repo: string) {
  return `/${encodePart(owner)}/${encodePart(repo)}/issues`
}

export const issuePath: PathFunction<{owner: string; repo: string; issueNumber: number}> = ({
  owner,
  repo,
  issueNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/issues/${issueNumber}`

export const issueCommentPath: PathFunction<{owner: string; repo: string; issueNumber: number; contentId: number}> = ({
  owner,
  repo,
  issueNumber,
  contentId,
}) => `/${encodePart(owner)}/${encodePart(repo)}/issues/${issueNumber}#issuecomment-${contentId}`

export const issueBodyPath: PathFunction<{owner: string; repo: string; issueNumber: number; contentId: number}> = ({
  owner,
  repo,
  issueNumber,
  contentId,
}) => `/${encodePart(owner)}/${encodePart(repo)}/issues/${issueNumber}#issue-${contentId}`

export const issueHovercardPath: PathFunction<{owner: string; repo: string; issueNumber: number}> = ({
  owner,
  repo,
  issueNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/issues/${issueNumber}/hovercard`

export const issueLinkedPullRequestHovercardPath: PathFunction<{
  owner: string
  repo: string
  pullRequestNumber: number
}> = ({owner, repo, pullRequestNumber}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/pull/${pullRequestNumber}/hovercard`

export const featurePreviews: PathFunction<{login: string}> = ({login}) =>
  `/users/${encodePart(login)}/feature_previews`

export function extractPathFromPathname(pathname: string, refName: string, defaultPath: string) {
  // We expect the pathname to be in the form of /owner/repo/action/branch/path
  // That's why we split the pathname by '/' and take the 4th element (after the action)
  const indexOfBranchAndPath = getIndexOfNth(pathname, '/', 4)
  const branchAndPath = pathname.substring(indexOfBranchAndPath)

  if (branchAndPath === `/${refName}`) {
    // there is no path so there is no trailing slash
    return ''
  } else if (branchAndPath.startsWith(`/${refName}/`)) {
    return branchAndPath.substring(refName.length + 2)
  } else {
    return defaultPath
  }
}

export function codeOfConductPath(owner: string, repo: string) {
  return `/${encodePart(owner)}/${encodePart(repo)}/community/code-of-conduct/new`
}

export function licenseSelectorPath(owner: string, repo: string) {
  return `/${encodePart(owner)}/${encodePart(repo)}/community/license/new`
}

export function securityPolicyPath(owner: string, repo: string) {
  return `/${encodePart(owner)}/${encodePart(repo)}/security/policy`
}

function getIndexOfNth(text: string, subString: string, index: number) {
  return text.split(subString, index).join(subString).length
}

export const repoTransferPath = (repo: Repository) =>
  `/${encodePart(`${repo.ownerLogin}/${repo.name}`)}/settings/transfer`

export const repositoryCheckNamePath = () => '/repositories/check-name'

export const repositoryCheckPath = () => '/repositories/check-proposed-repository'

export const repositoriesPath = () => '/repositories'

export const repoTransferTeamSuggestionsPath = (repo: Repository) =>
  `/${encodePart(`${repo.ownerLogin}/${repo.name}`)}/settings/transfer`

export const repoAbortTransferPath = (repo: Repository) =>
  `/${encodePart(`${repo.ownerLogin}/${repo.name}`)}/settings/abort_transfer`

export const repositoryImportsPath = () => '/new/import'

export const templateReposPath = () => `/repositories/new/templates`

export const repoOwnerItemsPath = () => '/repositories/forms/owner_items'

export const repoForkOwnerItemsPath = (repoId: number) => `/repositories/forms/fork_owner_items?repo_id=${repoId}`

export function repoOwnerDetailPath(owner: string): string
export function repoOwnerDetailPath(owner: string, action: 'transfer', repoId: number): string
export function repoOwnerDetailPath(owner: string, action?: 'transfer', repoId?: number): string {
  const base = `/repositories/forms/owner_detail`
  const params = new URLSearchParams({owner})

  if (action && repoId) {
    params.set('form', action)
    params.set('repo_id', repoId.toString())
  }

  return `${base}?${params.toString()}`
}

export const getContributorsPath = (repo: Repository) => `/repositories/${encodePart(repo.name)}/contributors/`

export const repoContributorsPath = (repo: Repository) =>
  `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/graphs/contributors`

export const repoAccessSettingsPath = (repo: Repository) =>
  `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/settings/access`

type PropertiesPathPrefix = 'organizations' | 'enterprises'

export function propertyDefinitionSettingsPath({
  pathPrefix,
  sourceName,
  propertyName,
}: {
  pathPrefix: PropertiesPathPrefix
  sourceName: string
  propertyName?: string
}) {
  const parts = ['', pathPrefix, encodePart(sourceName), 'settings']
  if (propertyName) {
    parts.push('custom-property')
    parts.push(encodePart(propertyName))
  } else {
    parts.push('custom-properties')
  }
  return parts.join('/')
}

export function customPropertyDetailsPath({
  pathPrefix,
  sourceName,
  propertyName,
}: {
  pathPrefix: PropertiesPathPrefix
  sourceName: string
  propertyName?: string
}) {
  const base = `/${pathPrefix}/${encodePart(sourceName)}/settings/custom-property`
  if (propertyName) {
    return `${base}/${encodePart(propertyName)}`
  } else {
    return base
  }
}

export const businessOrgCustomPropertyDetailsPath = ({
  business,
  org,
  propertyName,
}: {
  business: string
  org: string
  propertyName: string
}) =>
  `/enterprises/${encodePart(business)}/settings/custom-property/organizations/${encodePart(org)}/${encodePart(
    propertyName,
  )}`

export const promoteBusinessOrgCustomPropertyPath = ({
  business,
  org,
  propertyName,
}: {
  business: string
  org: string
  propertyName: string
}) =>
  `/enterprises/${encodePart(business)}/settings/custom-property/organizations/${encodePart(org)}/${encodePart(
    propertyName,
  )}/promote`

export function updateRepoPropertyValuesFromOrgPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/custom-properties/values`
}

export function updateRepoPropertyValuesFromRepoPath({org, repo}: {org: string; repo: string}) {
  return `/${encodePart(org)}/${encodePart(repo)}/settings/custom-properties/values`
}

export function listOrgReposPropertyValuesPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/custom-properties/list-repos-values`
}

export const checkPropertyUsagePath = ({
  pathPrefix,
  sourceName,
  propertyName,
}: {
  pathPrefix: PropertiesPathPrefix
  sourceName: string
  propertyName: string
}) =>
  ['', pathPrefix, encodePart(sourceName), 'settings', 'custom-properties-usage', encodePart(propertyName)].join('/')

export const checkPropertyNamePath = ({sourceName, propertyName}: {sourceName: string; propertyName: string}) =>
  `/enterprises/${encodePart(sourceName)}/settings/property_definition_name_check/${encodePart(propertyName)}`

export function repositoryPropertiesSettingsPath({org, repo}: {org: string; repo: string}) {
  return `/${encodePart(`${org}/${repo}`)}/settings/custom-properties`
}

export function getRepoPropertyAllowedValuesPath(props: {owner: string; propertyName: string}): string
export function getRepoPropertyAllowedValuesPath(props: {enterprise: string; propertyName: string}): string
export function getRepoPropertyAllowedValuesPath({
  propertyName,
  ...props
}: {owner: string; propertyName: string} | {enterprise: string; propertyName: string}): string {
  const params = new URLSearchParams(props)
  return `/repositories/custom-properties/${encodePart(propertyName)}/allowed-values?${params.toString()}`
}

export function forkPath({owner, repo}: {owner: string; repo: string}) {
  return `/${encodePart(`${owner}/${repo}`)}/fork`
}

export function forksListPath({owner, repo}: {owner: string; repo: string}) {
  return `/${encodePart(`${owner}/${repo}`)}/forks`
}

export function newRepoPath({org}: {org?: string} = {}) {
  if (org) {
    return `/organizations/${encodePart(org)}/repositories/new`
  }

  return `/new`
}

export function orgReposIndexPath({org}: {org: string}) {
  return `/orgs/${encodePart(org)}/repositories`
}

export function orgReposListPath({org}: {org: string}) {
  return `/orgs/${encodePart(org)}/repos_list`
}

export function reposPropertiesPath({orgs}: {orgs: string[]}) {
  const orgsWithEncoding = orgs.map(org => encodePart(org))
  const orgParamString = `orgs=${orgsWithEncoding.join(',')}`
  return `/repos/custom_properties?${orgParamString}`
}

export function orgTopicReposPath({topic, org}: {topic: string; org: string}) {
  return `/search?q=topic%3A${encodePart(topic)}+org%3A${encodePart(org)}&type=Repositories`
}

export function shortcutsPath(id?: string) {
  return `/users/shortcuts${id ? `/${id}` : ''}`
}

export function repoAttestationsIndexPath({
  repo,
  pagination = {before: null, after: null},
  perPage = 30,
  query,
}: {
  repo: Repository
  pagination?: {before?: string | null; after?: string | null}
  perPage?: number
  query?: string
}) {
  const parts = [repo.ownerLogin, repo.name, 'attestations']
  const params = []

  if (pagination) {
    if (pagination.after) {
      params.push(`after=${encodePart(pagination.after)}`)
    } else if (pagination.before) {
      params.push(`before=${encodePart(pagination.before)}`)
    }
  }

  if (query) {
    params.push(`q=${query}`)
  }

  // Don't add default values to the path/query-string
  if (perPage && perPage !== 30) {
    params.push(`per_page=${perPage}`)
  }

  return `/${parts.map(encodePart).join('/')}${params.length > 0 ? `?${params.join('&')}` : ''}`
}

export function repoAttestationShowPath({repo, attestationId}: {repo: Repository; attestationId: number | string}) {
  // Coerce attestationId to string as it's sometimes a number (SSR vs client-side hydration)
  return `/${[repo.ownerLogin, repo.name, 'attestations', attestationId.toString()].map(encodePart).join('/')}`
}

export function repoAttestationDownloadPath({repo, attestationId}: {repo: Repository; attestationId: number | string}) {
  // Coerce attestationId to string as it's sometimes a number (SSR vs client-side hydration)
  return `/${[repo.ownerLogin, repo.name, 'attestations', attestationId.toString(), 'download']
    .map(encodePart)
    .join('/')}`
}

export function branchesPath({repo}: {repo: Repository}) {
  return `/${[repo.ownerLogin, repo.name, 'branches'].map(encodePart).join('/')}`
}

export function tagsPath({repo}: {repo: Repository}) {
  return `/${[repo.ownerLogin, repo.name, 'tags'].map(encodePart).join('/')}`
}

export function orgOnboardingAdvancedSecurityPath({org}: {org: string}) {
  return `/orgs/${encodePart(org)}/organization_onboarding/advanced_security`
}

export function settingsEnterpriseAdvancedSecurityPath({enterprise}: {enterprise: string}) {
  return `/enterprises/${encodePart(enterprise)}/settings/advanced_security`
}

export function settingsEnterpriseSecurityProductsPath({enterprise}: {enterprise: string}) {
  return `/enterprises/${encodePart(enterprise)}/settings/security_analysis`
}

export function settingsEnterpriseSecurityProductsRepositoriesApplyPath({enterprise}: {enterprise: string}) {
  return `/enterprises/${encodePart(enterprise)}/settings/security_analysis/apply_configuration`
}

export function settingsEnterpriseSecurityProductsConfigurationSummaryPath({enterprise}: {enterprise: string}) {
  return `/enterprises/${encodePart(enterprise)}/settings/security_analysis/apply_confirmation_summary`
}

export function settingsOrgSecurityProductsPath({org, tip, q}: {org: string; tip?: string | null; q?: string | null}) {
  let path = `/organizations/${encodePart(org)}/settings/security_products`
  if (tip) {
    path += `?tip=${tip}`
  }
  if (q) {
    path += `${tip ? '&' : '?'}q=${encodePart(q)}`
  }
  return path
}

export function settingsOrgSecurityConfigurationsNewPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/configurations/new`
}

export function settingsOrgSecurityConfigurationsQuickSetupPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/quick_setup_configurations/new`
}

export function settingsOrgQuickSetupSecurityConfigurationsCreatePath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/quick_setup_configurations`
}

export function settingsOrgQuickSetupSecurityConfigurationPath({org, id}: {org: string; id: number}) {
  return `/organizations/${encodePart(org)}/settings/security_products/quick_setup_configurations/${id}`
}

export function settingsOrgSecurityConfigurationsCreatePath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/configurations`
}

export function settingsOrgSecurityConfigurationsEditPath({org, id}: {org: string; id: number}) {
  return `/organizations/${encodePart(org)}/settings/security_products/configurations/edit/${id}`
}

export function settingsOrgSecurityConfigurationsUpdatePath({org, id}: {org: string; id: number}) {
  return `/organizations/${encodePart(org)}/settings/security_products/configurations/${id}`
}

export function settingsOrgSecurityProductsRepositories({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/repositories`
}

export function settingsOrgSecurityProductsRepositoriesConfigurationSummaryPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/repositories/apply_confirmation_summary`
}

export function settingsOrgSecurityProductsRepositoriesApplyPath({org, id}: {org: string; id: number}) {
  return `/organizations/${encodePart(org)}/settings/security_products/configuration/${id}/repositories`
}

export function settingsOrgSecurityProductsRepositoriesDeletePath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/configuration/repositories`
}

export function settingsOrgSecurityProductsRepositoriesCount({org, id}: {org: string; id: number}) {
  return `/organizations/${encodePart(org)}/settings/security_products/configuration/${id}/repositories_count`
}

export function settingsOrgSecurityConfigurationsViewPath({
  org,
  id,
  tip,
}: {
  org: string
  id: number
  tip?: string | null
}) {
  let path = `/organizations/${encodePart(org)}/settings/security_products/configurations/view/${id}`
  if (tip) {
    path += `?tip=${tip}`
  }
  return path
}

export function settingsOrgSecurityProductsRepositoriesGhasLicenseSummaryPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/repositories/advanced_security_license_summary`
}

export function settingsOrgSecurityProductsEnablementInProgressPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/in_progress`
}

export function settingsOrgSecurityProductsRefreshPath({org}: {org: string}) {
  return `/organizations/${encodePart(org)}/settings/security_products/refresh`
}

export function settingsOrgSecurityProductsActionsRunnersLabelsPath(org: string) {
  return `/organizations/${encodePart(org)}/settings/security_products/actions_runners_labels`
}

export function enterpriseSecurityCoveragePath({business, query}: {business: string; query?: string}) {
  const basePath = `/enterprises/${encodePart(business)}/security/coverage`
  if (!query) return basePath

  const params = new URLSearchParams()
  params.append('query', query)
  return `${basePath}?${params.toString()}`
}

export function settingsBusinessSecurityAnalysisPath({business}: {business: string}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis`
}

export function settingsBusinessSecurityAnalysisPoliciesPath({business}: {business: string}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis_policies`
}

export function settingsBusinessSecurityAnalysisActionsRunnersLabelsPath(business: string) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis/actions_runners_labels`
}

export function settingsBusinessSecurityAnalysisConfigurationsNewPath({business}: {business: string}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis/configurations/new`
}

export function settingsBusinessSecurityAnalysisConfigurationsCreatePath({business}: {business: string}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis/configurations`
}

export function settingsBusinessSecurityAnalysisConfigurationsEditPath({business, id}: {business: string; id: number}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis/configurations/${id}/edit`
}

export function settingsBusinessSecurityAnalysisConfigurationsUpdatePath({
  business,
  id,
}: {
  business: string
  id: number
}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis/configurations/${id}`
}

export function settingsBusinessSecurityAnalysisConfigurationsViewPath({business, id}: {business: string; id: number}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis/configurations/${id}/view`
}

export function settingsBusinessSecurityAnalysisConfigurationRepositoriesCount({
  business,
  id,
}: {
  business: string
  id: number
}) {
  return `/enterprises/${encodePart(business)}/settings/security_analysis/configurations/${id}/repositories_count`
}

export function settingsUserSecurityProductsPath() {
  return `/users/settings/security_products`
}

export function settingsUserSecurityProductsRepositoriesCount({id}: {id: number}) {
  return `/users/settings/security_products/configuration/${id}/repositories_count`
}

export function settingsUserSecurityConfigurationsNewPath() {
  return `/users/settings/security_products/configurations/new`
}

export function settingsUserSecurityConfigurationsEditPath({id}: {id: number}) {
  return `/users/settings/security_products/configurations/edit/${id}`
}

export function settingsUserSecurityConfigurationsViewPath({id}: {id: number}) {
  return `/users/settings/security_products/configurations/view/${id}`
}

export function settingsUserSecurityConfigurationsCreatePath() {
  return `/users/settings/security_products/configurations`
}

export function settingsUserSecurityConfigurationsUpdatePath({id}: {id: number}) {
  return `/users/settings/security_products/configurations/${id}`
}

export function settingsUserSecurityConfigurationApplyPath({id}: {id: number}) {
  return `/users/settings/security_products/configuration/${id}/apply_configuration`
}

export function settingsUserSecurityConfigurationSummaryPath() {
  return `/users/settings/security_products/configuration/apply_confirmation_summary`
}

export function settingsUserSecurityConfigurationDetachPath() {
  return `/users/settings/security_products/configuration/detach_configuration`
}

export function contactSalesPath() {
  return '/contact-sales'
}

export function contactSalesThanksPath() {
  return `/enterprise/contact/thanks`
}

export const contactPath = () => '/contact'

export function reportAbusePath({report, contentURL}: {report: string; contentURL?: string}) {
  const reportParamString = new URLSearchParams({report})
  if (contentURL) {
    reportParamString.append('report_content_url', contentURL)
  }
  return `/contact/report-abuse?${reportParamString}`
}

export function repoSettingsSecurityAnalysisPath({repo}: {repo: Repository}) {
  return `/${encodePart(`${repo.ownerLogin}/${repo.name}`)}/settings/security_analysis`
}

export function dismissOrganizationNoticePath({org}: {org: string}) {
  return `/orgs/${encodePart(org)}/dismiss_notice`
}

export function dismissUserNoticePath({noticeName}: {noticeName: string}) {
  return `/settings/dismiss-notice/${noticeName}`
}

export const enterprisePath: PathFunction<{slug: string}> = ({slug}) => `/enterprises/${slug}`

export const enterpriseRolesPath: PathFunction<{slug: string}> = ({slug}) =>
  `/enterprises/${encodePart(slug)}/enterprise_roles`

export function enterpriseRoleAssignmentsPath({slug, page, query}: {slug: string; page?: number; query?: string}) {
  const basePath = `/enterprises/${encodePart(slug)}/enterprise_role_assignments`
  const params = new URLSearchParams()
  if (page) {
    params.append('page', page.toString())
  }
  if (query) {
    params.append('query', query)
  }

  const paramString = params.toString()
  return paramString ? `${basePath}?${paramString}` : basePath
}

export function stafftoolsEnterpriseRoleAssignmentsPath({
  slug,
  page,
  query,
}: {
  slug: string
  page?: number
  query?: string
}) {
  const basePath = `/stafftools/enterprises/${encodePart(slug)}/custom_roles/enterprise_role_assignments`
  const params = new URLSearchParams()
  if (page) {
    params.append('page', page.toString())
  }
  if (query) {
    params.append('query', query)
  }

  const paramString = params.toString()
  return paramString ? `${basePath}?${paramString}` : basePath
}

export const destroyEnterpriseRoleAssignmentPath: PathFunction<{
  slug: string
  actorId: number
  actorType: string
  roleId: number
}> = ({slug, actorId, actorType, roleId}) => {
  return `/enterprises/${encodePart(slug)}/enterprise_role_assignments/${actorType}/${actorId}/${roleId}`
}

export const destroyOrgRoleAssignmentPath: PathFunction<{
  slug: string
  actorId: number
  actorType: string
  roleId: number
}> = ({slug, actorId, actorType, roleId}) => {
  return `/organizations/${encodePart(slug)}/settings/org_role_assignments/${actorType}/${actorId}/${roleId}`
}

export const newEnterpriseRoleAssignmentPath: PathFunction<{slug: string}> = ({slug}) =>
  `/enterprises/${encodePart(slug)}/enterprise_role_assignments/new`

export const newEnterpriseRoleAssignmentQueryActorsPath: PathFunction<{slug: string}> = ({slug}) =>
  `/enterprises/${encodePart(slug)}/enterprise_role_assignment_queries`

export function orgRoleAssignmentsPath({slug, page, query}: {slug: string; page?: number; query?: string}) {
  const basePath = `/organizations/${encodePart(slug)}/settings/org_role_assignments`
  const params = new URLSearchParams()
  if (page) {
    params.append('page', page.toString())
  }
  if (query) {
    params.append('query', query)
  }

  const paramString = params.toString()
  return paramString ? `${basePath}?${paramString}` : basePath
}

export const newOrgRoleAssignmentPath: PathFunction<{slug: string}> = ({slug}) =>
  `/organizations/${slug}/settings/org_role_assignments/new`

export const newOrgRoleAssignmentQueryActorsPath: PathFunction<{slug: string}> = ({slug}) =>
  `/organizations/${slug}/settings/org_role_assignment_queries`

export function stafftoolsOrgRoleAssignmentsPath({slug, page, query}: {slug: string; page?: number; query?: string}) {
  const basePath = `/stafftools/users/${encodePart(slug)}/org_role_assignments`
  const params = new URLSearchParams()
  if (page) {
    params.append('page', page.toString())
  }
  if (query) {
    params.append('query', query)
  }

  const paramString = params.toString()
  return paramString ? `${basePath}?${paramString}` : basePath
}

export function emailSubscriptionTopicsByEmailPath(email: string) {
  const emailParamString = new URLSearchParams({email})
  return `/settings/emails/subscriptions/topics_by_email?${emailParamString}`
}

export function emailSubscriptionTopicsByParamsPath<TopicsParams extends Record<string, string>>(
  topicSettingParams: TopicsParams,
) {
  const topicSettingParamString = new URLSearchParams(topicSettingParams)
  return `/settings/emails/subscriptions/topics_by_params?${topicSettingParamString}`
}

export function emailSubscriptionNewLinkPath() {
  return '/settings/emails/subscriptions/link-request/new'
}

export const userPRFileTreeVisibilitySettingPath: PathFunction<{login: string}> = ({login}) =>
  `/users/${encodePart(login)}/pulls/settings/file_tree_visibility`

export const codeScanningAlertPath: PathFunction<{owner: string; repo: string; alertNumber: number}> = ({
  owner,
  repo,
  alertNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/code-scanning/${alertNumber}`

export const codeScanningAlertAssigneesPath: PathFunction<{owner: string; repo: string; alertNumber: number}> = ({
  owner,
  repo,
  alertNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/code-scanning/${alertNumber}/assignees`

export const codeScanningAvailableAssigneesPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/code-scanning/available-assignees`

export const codeScanningAlertTrackingLinksPath: PathFunction<{owner: string; repo: string; alertNumber: number}> = ({
  owner,
  repo,
  alertNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/code-scanning/${alertNumber}/tracking-links`

export const orgCodeScanningPath: PathFunction<{
  org: string
  query?: string
  sourceCampaignNumber?: number
}> = ({org, query, sourceCampaignNumber}) => {
  const params = []
  if (query) {
    params.push(`query=${encodePart(query)}`)
  }
  if (sourceCampaignNumber) {
    params.push(`source_campaign_number=${sourceCampaignNumber}`)
  }
  return `/orgs/${encodePart(org)}/security/alerts/code-scanning${params.length > 0 ? `?${params.join('&')}` : ''}`
}

export const codeScanningOrgAlertListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/alerts/code-scanning/alert-list`

export const codeScanningOrgAlertGroupListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/alerts/code-scanning/alert-group-list`

export const codeScanningOrgRepositoryListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/alerts/code-scanning/repository-list.json`

export const codeScanningOrgRuleListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/alerts/code-scanning/rule-list.json`

export const codeScanningOrgTagListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/alerts/code-scanning/tag-list.json`

export const codeScanningOrgToolListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/alerts/code-scanning/tool-list.json`

export const secretScanningAlertPath: PathFunction<{owner: string; repo: string; alertNumber: number}> = ({
  owner,
  repo,
  alertNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/secret-scanning/${alertNumber}`

export function secretScanningOrgAlertListPath({org}: {org: string}) {
  return `/orgs/${encodePart(org)}/security/alerts/secret-scanning/alert-list`
}

export function secretScanningOrgAlertGroupListPath({org}: {org: string}) {
  return `/orgs/${encodePart(org)}/security/alerts/secret-scanning/alert-group-list`
}

export const secretScanningOrgRepositoryListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/alerts/secret-scanning/repository-list.json`

export const securityOverviewPath: PathFunction<{org: string}> = ({org}) => `/orgs/${encodePart(org)}/security/overview`

export const securityCenterOptionsPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/options`

export const codeQualityIndexPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/quality`

export const codeQualityShowPath: PathFunction<{owner: string; repo: string; ruleId: string}> = ({
  owner,
  repo,
  ruleId,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/quality/rules/${encodeURIComponent(ruleId)}`

export const codeQualitySettingsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/settings/code-quality`

export const codeQualityAIFindingsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/quality/ai-findings`

export const codeQualityAIFindingsListFindingsPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/quality/ai-findings/findings`

export const codeQualityRulesPath: PathFunction<{
  owner: string
  repo: string
  category?: string
  severity?: string
  language?: string
}> = ({owner, repo, category, severity, language}) => {
  const params = []
  if (category) {
    params.push(`category=${encodeURIComponent(category.toLowerCase())}`)
  }
  if (severity) {
    params.push(`severity=${encodeURIComponent(severity.toLowerCase())}`)
  }
  if (language) {
    params.push(`language=${encodeURIComponent(language.toLowerCase())}`)
  }

  return `/${encodePart(owner)}/${encodePart(repo)}/security/quality/rules${
    params.length > 0 ? `?${params.join('&')}` : ''
  }`
}

export const codeQualityRuleFilesPath: PathFunction<{owner: string; repo: string; ruleId: string}> = ({
  owner,
  repo,
  ruleId,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/quality/rules/${encodeURIComponent(ruleId)}/files`

export const codeQualityRuleFindingsPath: PathFunction<{owner: string; repo: string; ruleId: string}> = ({
  owner,
  repo,
  ruleId,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/quality/rules/${encodeURIComponent(ruleId)}/findings`

export const codeQualityRuleFindingStatePath: PathFunction<{
  owner: string
  repo: string
  findingStableId: string
}> = ({owner, repo, findingStableId}) => {
  return `/${encodePart(owner)}/${encodePart(repo)}/security/quality/findings/${encodeURIComponent(
    findingStableId,
  )}/state`
}

export const codeQualityRuleFindingsAutofixesCreatePath: PathFunction<{
  owner: string
  repo: string
  ruleId: string
  findingStableId: string
}> = ({owner, repo, ruleId, findingStableId}) => {
  return `/${encodePart(owner)}/${encodePart(repo)}/security/quality/rules/${encodeURIComponent(
    ruleId,
  )}/findings/${encodeURIComponent(findingStableId)}/autofixes`
}

export const codeQualityFindingsAutofixCommitPath: PathFunction<{
  owner: string
  repo: string
  findingStableId: string
}> = ({owner, repo, findingStableId}) => {
  return `/${encodePart(owner)}/${encodePart(repo)}/security/quality/findings/${encodeURIComponent(
    findingStableId,
  )}/autofix/commit`
}

export const codeQualityAIFindingsFixCommitPath: PathFunction<{owner: string; repo: string}> = ({owner, repo}) => {
  return `/${encodePart(owner)}/${encodePart(repo)}/security/quality/ai-findings/commits`
}

export const orgCodeQualityOptionsPath: PathFunction<{org: string; optionsType: string; name?: string}> = ({
  org,
  optionsType,
  name,
}) => {
  const params = [`options-type=${encodePart(optionsType)}`]
  if (name) {
    params.push(`name=${encodePart(name)}`)
  }
  return `/orgs/${encodePart(org)}/security/quality/options${params.length > 0 ? `?${params.join('&')}` : ''}`
}

export const securityCampaignOrgCampaignsPath: PathFunction<{org: string; state?: 'open' | 'closed' | 'draft'}> = ({
  org,
  state,
}) => {
  const params = []
  if (state) {
    params.push(`state=${state}`)
  }

  return `/orgs/${encodePart(org)}/security/campaigns${params.length > 0 ? `?${params.join('&')}` : ''}`
}

export const securityCampaignsOrgNewCampaignPath: PathFunction<{
  org: string
  query?: string
  templateId?: string
  sourceCampaignNumber?: number
  alertType?: 'code_scanning' | 'secret_scanning'
}> = ({org, query, templateId, sourceCampaignNumber, alertType}) => {
  const params = []
  if (query) {
    params.push(`query=${encodePart(query)}`)
  }
  if (templateId) {
    params.push(`template=${encodePart(templateId)}`)
  }
  if (sourceCampaignNumber) {
    params.push(`source_campaign_number=${sourceCampaignNumber}`)
  }
  if (alertType) {
    params.push(`alert_type=${alertType}`)
  }

  return `/orgs/${encodePart(org)}/security/campaigns/new${params.length > 0 ? `?${params.join('&')}` : ''}`
}

export const securityCampaignOrgCampaignsCountsPath: PathFunction<{org: string; alertType?: string}> = ({
  org,
  alertType,
}) => {
  const params = []
  if (alertType) {
    params.push(`alert_type=${alertType}`)
  }
  return `/orgs/${encodePart(org)}/security/campaigns/counts${params.length > 0 ? `?${params.join('&')}` : ''}`
}

export const securityCampaignOrgClosedCampaignsPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns/closed`

export const securityCampaignOrgCampaignCreatePath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns`

export const securityCampaignOrgCampaignCreateDraftPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns/drafts`

export const securityCampaignOrgCampaignEditPath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}`

export const securityCampaignOrgCampaignDeletePath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}`

export const securityCampaignOrgCampaignClosePath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}/close`

export const securityCampaignOrgCampaignReopenPath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}/reopen`

export const securityCampaignOrgCampaignEditDraftPath: PathFunction<{
  org: string
  securityCampaignNumber: number
}> = ({org, securityCampaignNumber}) => `/orgs/${encodePart(org)}/security/campaigns/drafts/${securityCampaignNumber}`

export const securityCampaignOrgDraftCampaignPublishPath: PathFunction<{
  org: string
  securityCampaignNumber: number
}> = ({org, securityCampaignNumber}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}/publish`

export const securityCampaignOrgCampaignAlertsSummaryPath: PathFunction<{
  org: string
}> = ({org}) => `/orgs/${encodePart(org)}/security/campaigns/alerts/summary`

export const securityCampaignOrgCampaignPath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}`

export const securityCampaignOrgCampaignPublishPath: PathFunction<{
  org: string
  query: string
  campaignName?: string
  campaignDescription?: string
  sourceCampaignNumber?: number
  alertType?: string
}> = ({org, query, campaignName, campaignDescription, sourceCampaignNumber, alertType}) => {
  const params = [`query=${encodePart(query)}`]

  if (campaignName) {
    params.push(`campaign_name=${encodePart(campaignName)}`)
  }
  if (campaignDescription) {
    params.push(`campaign_description=${encodePart(campaignDescription)}`)
  }
  if (sourceCampaignNumber) {
    params.push(`source_campaign_number=${sourceCampaignNumber}`)
  }
  if (alertType) {
    params.push(`alert_type=${alertType}`)
  }

  return `/orgs/${encodePart(org)}/security/campaigns/publish?${params.join('&')}`
}

export const securityCampaignOrgAlertsPath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}/alerts`

export const securityCampaignOrgAlertsGroupsPath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}/alerts-groups`

export const securityCampaignOrgRepositoriesSummaryPath: PathFunction<{
  org: string
  securityCampaignNumber: number
}> = ({org, securityCampaignNumber}) =>
  `/orgs/${encodePart(org)}/security/campaigns/${securityCampaignNumber}/repositories-summary`

export const securityCampaignOrgOpenListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns/open`

export const securityCampaignOrgClosedPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns/closed`

export const securityCampaignOrgClosedListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns/closed`

export const securityCampaignOrgDraftListPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns/drafts`

export const securityCampaignOrgDeleteDraftPath: PathFunction<{
  org: string
  securityCampaignNumber: number
}> = ({org, securityCampaignNumber}) => `/orgs/${encodePart(org)}/security/campaigns/drafts/${securityCampaignNumber}`

export const securityCampaignOrgPublishDraftPath: PathFunction<{org: string; securityCampaignNumber: number}> = ({
  org,
  securityCampaignNumber,
}) => `/orgs/${encodePart(org)}/security/campaigns/drafts/${securityCampaignNumber}/publish`

export const securityCampaignOrgManagersPath: PathFunction<{org: string}> = ({org}) =>
  `/orgs/${encodePart(org)}/security/campaigns/managers`

export const campaignRepoPath: PathFunction<{owner: string; repo: string; campaignNumber: number}> = ({
  owner,
  repo,
  campaignNumber,
}) => `/${encodePart(owner)}/${encodePart(repo)}/security/campaigns/${campaignNumber}`

export const securityCampaignRepoAlertsPath: PathFunction<{
  owner: string
  repo: string
  securityCampaignNumber: number
}> = ({owner, repo, securityCampaignNumber}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/campaigns/${securityCampaignNumber}/alerts`

export const securityCampaignRepoAssignToCopilotPath: PathFunction<{
  owner: string
  repo: string
  securityCampaignNumber: number
}> = ({owner, repo, securityCampaignNumber}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/campaigns/${securityCampaignNumber}/assign-to-copilot`

export const securityCampaignRepoCheckForNewFixesPath: PathFunction<{
  owner: string
  repo: string
  securityCampaignNumber: number
}> = ({owner, repo, securityCampaignNumber}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/campaigns/${securityCampaignNumber}/check-for-new-fixes`

export const securityCampaignRepoCreateBranchPath: PathFunction<{
  owner: string
  repo: string
  securityCampaignNumber: number
}> = ({owner, repo, securityCampaignNumber}) =>
  `/${encodePart(owner)}/${encodePart(repo)}/security/campaigns/${securityCampaignNumber}/branches`

export function codeScanningDismissAlertsPath({
  owner,
  repo,
  isDelegatedAlertEnabled,
}: {
  owner: string
  repo: string
  isDelegatedAlertEnabled: boolean
}) {
  const basePath = `/${encodePart(owner)}/${encodePart(repo)}/security/code-scanning/alert-dismissal`
  return isDelegatedAlertEnabled ? `${basePath}/create-request` : `${basePath}/close-alerts`
}

export function secretScanningTokenResolvePath(owner: string, repo: string) {
  return `/${encodePart(owner)}/${encodePart(repo)}/security/secret-scanning/resolve-react`
}

export function orgRepositoriesPath({owner, query}: {owner: string; query: string}) {
  return `/orgs/${encodePart(owner)}/repositories?q=${encodePart(query)}`
}

export const businessOrgsSuggestionsPath = ({businessSlug}: {businessSlug: string}) =>
  `/enterprises/${encodePart(businessSlug)}/organizations/suggestions`

export const businessOrgsSuggestionsValidatePath = ({businessSlug}: {businessSlug: string}) =>
  `/enterprises/${encodePart(businessSlug)}/organizations/suggestions/validate`

export const businessTeamCheckNamePath = ({businessSlug}: {businessSlug: string}) =>
  `/enterprises/${encodePart(businessSlug)}/check_team_name`

export const codespacesPath = () => '/codespaces/'

export const loginPath = ({returnTo}: {returnTo?: string} = {}) => {
  return `/login${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''}`
}

export const orgBillingSettingsPath = ({org}: {org: string}) => {
  return `/organizations/${encodePart(org)}/settings/billing/summary`
}

export const orgBillingUsagePath = ({org, query}: {org: string; query: string}) => {
  return `/organizations/${encodePart(org)}/settings/billing/usage?query=${encodePart(query)}`
}

export const businessBillingSettingsPath = ({businessSlug}: {businessSlug: string}) => {
  return `/enterprises/${encodePart(businessSlug)}/settings/billing`
}

export const businessLicensingPath = ({businessSlug}: {businessSlug: string}) => {
  return `/enterprises/${encodePart(businessSlug)}/licensing`
}

export const repositoryGroupPath = ({basePath, groupPath}: {basePath: string; groupPath: string}) => {
  return encodePart(`${basePath}/${groupPath}`)
}

export const pullRequestApplyAutomatedSuggestionPath = ({
  basePath,
  automatedReviewCommentId,
}: {
  basePath: string
  automatedReviewCommentId: string
}) => {
  return `${basePath}/automated_review_comments/${automatedReviewCommentId}/apply_suggestion`
}

export const pullRequestDismissAutomatedReviewCommentPath = ({
  basePath,
  automatedReviewCommentId,
}: {
  basePath: string
  automatedReviewCommentId: string
}) => {
  return `${basePath}/automated_review_comments/${automatedReviewCommentId}/dismiss`
}

export const pullRequestReopenAutomatedReviewCommentPath = ({
  basePath,
  automatedReviewCommentId,
}: {
  basePath: string
  automatedReviewCommentId: string
}) => {
  return `${basePath}/automated_review_comments/${automatedReviewCommentId}/reopen`
}

export const pullRequestFeedbackAutomatedReviewCommentPath = ({
  basePath,
  automatedReviewCommentId,
}: {
  basePath: string
  automatedReviewCommentId: string
}) => {
  return `${basePath}/automated_review_comments/${automatedReviewCommentId}/feedback`
}
